/**
 * 认证相关工具函数
 */

// 清除登录状态
export const clearAuthState = (): void => {
  document.cookie = "anzir_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  localStorage.removeItem("anzir_user");
  localStorage.removeItem("anzir_refresh_token");
};

// 跳转到登录页
export const redirectToLogin = (message?: string): void => {
  // 保存当前URL作为重定向URL，便于登录成功后返回
  const currentPath = window.location.pathname + window.location.search;
  // 如果提供了消息，添加为message参数，同时添加redirectUrl参数
  const url = `/login?redirectUrl=${encodeURIComponent(currentPath)}`;

  // 如果当前不在登录页，才进行跳转
  if (window.location.pathname !== '/login') {
    window.location.href = url;
  }
};

// 处理身份验证失效
export const handleAuthFailure = (statusCode: number, message?: string): void => {
  // 只处理401和418状态码
  if (statusCode === 401 || statusCode === 418) {
    clearAuthState();
    redirectToLogin(message || '登录已过期，请重新登录');
  }
};

// 检查是否已登录
export const isAuthenticated = (): boolean => {
  const token = document.cookie.split('; ').find(row => row.startsWith('anzir_token='))?.split('=')[1];
  const userData = localStorage.getItem("anzir_user");
  return !!(token && userData);
};

/**
 * 第三方登录工具函数
 */

/**
 * 获取Google登录URL
 * @returns Google OAuth登录URL
 */
export function getGoogleAuthUrl(): string {
  const clientId = import.meta.env.VITE_APP_CLIENT_GOOGLE;
  const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`;
  const scope = import.meta.env.VITE_GOOGLE_SCOPE || 'email profile';

  // 添加必要的参数，确保符合Google最新OAuth要求
  return `https://accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code` +
    `&scope=${encodeURIComponent(scope)}` +
    `&access_type=offline` +
    `&prompt=consent` +
    `&include_granted_scopes=true`;
}

/**
 * 获取Apple登录URL
 * @returns Apple OAuth登录URL
 */
export function getAppleAuthUrl(): string {
  const clientId = import.meta.env.VITE_APP_CLIENT_APPLE;
  const redirectUri = import.meta.env.VITE_APPLE_REDIRECT_URI || `${window.location.origin}/auth/apple/callback`;
  const scope = import.meta.env.VITE_APPLE_SCOPE || 'name email';

  return `https://appleid.apple.com/auth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&response_mode=form_post`;
}

/**
 * 获取URL中的授权码
 * @returns 认证码或null
 */
export function getAuthorizationCodeFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('code');
}

/**
 * 获取设备信息
 * @returns 设备信息对象
 */
export function getDeviceInfo() {
  return {
    deviceId: navigator.userAgent + window.location.hostname,
    deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'MOBILE' : 'PC',
    appVersion: import.meta.env.VITE_APP_VERSION || '0.0.1'
  };
}

/**
 * 将认证方法字符串转换为后端需要的格式
 * @param provider 提供商名称
 * @returns 标准化的认证方法
 */
export function normalizeAuthMethod(provider: string): string {
  const providerMap: Record<string, string> = {
    'google': 'GOOGLE_AUTH_CODE_FLOW',
    'apple': 'APPLE_AUTH_CODE_FLOW'
  };

  return providerMap[provider.toLowerCase()] || 'UNKNOWN_AUTH_METHOD';
}
